/**
 * 全站语言单一事实来源（SSOT）。
 * UI 切换、vue-i18n、Skill API、HTTP、验证码等均通过 getAppLocale / setAppLocale 读写。
 */
export type AppLocale = 'en' | 'zh-CN' | 'zh-TW'

/** 与 vue-i18n、getLangType、H5 langType 约定一致 */
export type VueI18nLangKey = 'zhCn' | 'zhTc' | 'en'

export const LANDING_LOCALE_STORAGE_KEY = 'landing-locale'
export const VUE_I18N_LANG_STORAGE_KEY = 'lang'

export const DEFAULT_APP_LOCALE: AppLocale = 'zh-TW'

const SUPPORTED: AppLocale[] = ['en', 'zh-CN', 'zh-TW']

/** 内存缓存，避免非 Vue 模块重复解析 storage */
let cachedLocale: AppLocale | null = null

export function normalizeAppLocale(raw: string): AppLocale | undefined {
  const v = raw.trim()
  if (SUPPORTED.includes(v as AppLocale)) return v as AppLocale
  return undefined
}

export function toVueI18nKey(locale: AppLocale): VueI18nLangKey {
  if (locale === 'zh-CN') return 'zhCn'
  if (locale === 'zh-TW') return 'zhTc'
  return 'en'
}

/** SkillHub Accept-Language 首段 */
export function toAcceptLanguage(locale: AppLocale): string {
  return locale
}

/** 网关 / getLangType / H5 langType 等 legacy 取值 */
export function toLegacyLangType(locale: AppLocale): VueI18nLangKey {
  return toVueI18nKey(locale)
}

/** 腾讯验证码 userLanguage */
export function toTencentCaptchaLang(locale: AppLocale): 'zh-hk' | 'zhCn' | 'en' {
  if (locale === 'zh-TW') return 'zh-hk'
  if (locale === 'en') return 'en'
  return 'zhCn'
}

function readLocaleFromStorage(): AppLocale | null {
  if (typeof globalThis.localStorage === 'undefined') return null
  const landing = normalizeAppLocale(globalThis.localStorage.getItem(LANDING_LOCALE_STORAGE_KEY) ?? '')
  if (landing) return landing

  const langRaw = globalThis.localStorage.getItem(VUE_I18N_LANG_STORAGE_KEY) ?? ''
  const lang = langRaw.trim().replace(/[_-]/g, '').toLowerCase()
  if (lang === 'zhcn' || lang === 'zhhans') return 'zh-CN'
  if (lang === 'zhtc' || lang === 'zhtw' || lang === 'zhhk' || lang === 'zhhant') return 'zh-TW'
  if (lang === 'en' || lang === 'enus') return 'en'
  return null
}

function persistLocale(locale: AppLocale): void {
  try {
    globalThis.localStorage?.setItem(LANDING_LOCALE_STORAGE_KEY, locale)
    globalThis.localStorage?.setItem(VUE_I18N_LANG_STORAGE_KEY, toVueI18nKey(locale))
  } catch {
    /* 隐私模式等忽略 */
  }
}

/**
 * 启动时对齐主从 storage：以 landing-locale 为准修复 lang。
 */
export function reconcileLocaleStorage(): AppLocale {
  const resolved = readLocaleFromStorage() ?? DEFAULT_APP_LOCALE
  persistLocale(resolved)
  cachedLocale = resolved
  return resolved
}

/**
 * 读取当前语言（内存 → storage → 默认繁体）。
 */
export function getAppLocale(): AppLocale {
  if (cachedLocale) return cachedLocale
  const fromStorage = readLocaleFromStorage()
  const locale = fromStorage ?? DEFAULT_APP_LOCALE
  cachedLocale = locale
  return locale
}

/**
 * 唯一写入入口：切换语言时调用。
 */
export function setAppLocale(locale: AppLocale): void {
  if (!SUPPORTED.includes(locale)) return
  cachedLocale = locale
  persistLocale(locale)
}
